import React from 'react';
import {
  filterOffersOptionList,
  getFilterOptionDisplayLabel,
  getFilterOptionSelectedLabel,
  getSelectedFilterLabel,
} from '../../table-data-detail.utils';
import { InstantTooltip } from '../../../components/instant-tooltip';
import { withFilterFieldBase } from './withFilterFieldBase';
import type { LookupFilterFieldProps } from './types';

// Cada consulta de autocompletado recorre tablas de millones de filas. Se
// agrupan las pulsaciones para no lanzar una petición por tecla, y se exige un
// mínimo de caracteres: el término vacío obligaba al backend a recorrer la
// tabla completa.
const AUTOCOMPLETE_DEBOUNCE_MS = 350;
const AUTOCOMPLETE_MIN_CHARS = 2;

const LookupFilterFieldContent = ({
  filter,
  state,
  inputClassName,
  filtersDisabled,
  updateFilterState,
  fetchFilterOptions,
}: LookupFilterFieldProps) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const chipsRef = React.useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
  const [chipsExpanded, setChipsExpanded] = React.useState(false);
  const [chipsOverflow, setChipsOverflow] = React.useState(false);
  const fetchTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cancela la consulta pendiente si el campo se desmonta antes de dispararla.
  React.useEffect(
    () => () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
    },
    []
  );

  const scheduleFetchOptions = React.useCallback(
    (term: string) => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
      if (term.trim().length < AUTOCOMPLETE_MIN_CHARS) return;

      fetchTimerRef.current = setTimeout(() => {
        void fetchFilterOptions(filter, term);
      }, AUTOCOMPLETE_DEBOUNCE_MS);
    },
    [filter, fetchFilterOptions]
  );

  React.useEffect(() => {
    const node = chipsRef.current;
    if (!node) {
      setChipsOverflow(false);
      return;
    }
    const check = () => {
      if (chipsExpanded) return;
      setChipsOverflow(node.scrollHeight > node.clientHeight + 2);
    };
    check();
    const observer = new ResizeObserver(check);
    observer.observe(node);
    return () => observer.disconnect();
  }, [state?.selectedValues.length, chipsExpanded]);
  const inputValue = state?.displayValue ?? state?.inputValue ?? '';
  const searchMode = state?.searchMode ?? 'contains';

  const findMatchedItem = React.useCallback(
    (rawValue: string) => {
      return (state?.items ?? []).find(item => {
        const displayLabel = getFilterOptionDisplayLabel(item);
        return rawValue === displayLabel || rawValue === item.label || rawValue === item.value;
      });
    },
    [state?.items]
  );

  const filteredItems = React.useMemo(() => {
    const items = state?.items ?? [];
    const query = inputValue.trim().toLowerCase();
    if (!query) return items;
    if (searchMode === 'startsWith') {
      const startsWithMatches: typeof items = [];
      const remainingItems: typeof items = [];

      items.forEach(item => {
        const displayLabel = getFilterOptionDisplayLabel(item).toLowerCase();
        const val = (item.value ?? '').toLowerCase();
        const lbl = (item.label ?? '').toLowerCase();
        const matchesPrefix =
          displayLabel.startsWith(query) || val.startsWith(query) || lbl.startsWith(query);

        if (matchesPrefix) {
          startsWithMatches.push(item);
          return;
        }

        remainingItems.push(item);
      });

      return [...startsWithMatches, ...remainingItems];
    }

    return items.filter(item => {
      const displayLabel = getFilterOptionDisplayLabel(item).toLowerCase();
      return displayLabel.includes(query);
    });
  }, [state?.items, inputValue, searchMode]);

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleAddAll = () => {
    if (!filter.multiple) return;
    updateFilterState(filter.id, current => {
      const newValues = [...current.selectedValues];
      const newLabels = { ...(current.selectedLabels ?? {}) };
      for (const item of filteredItems) {
        if (!newValues.includes(item.value)) {
          newValues.push(item.value);
          newLabels[item.value] = getFilterOptionSelectedLabel(item);
        }
      }
      return {
        ...current,
        selectedValues: newValues,
        selectedLabels: newLabels,
        touched: true,
      };
    });
    setIsDropdownOpen(false);
  };

  const handleRemoveAll = () => {
    updateFilterState(filter.id, current => ({
      ...current,
      selectedValues: [],
      selectedLabels: {},
      touched: true,
    }));
  };

  const handleSelectItem = (item: (typeof filteredItems)[number]) => {
    const selectedLabel = getFilterOptionSelectedLabel(item);

    if (filter.multiple) {
      updateFilterState(filter.id, current => {
        if (current.selectedValues.includes(item.value)) {
          return { ...current, touched: true };
        }
        return {
          ...current,
          selectedValues: [...current.selectedValues, item.value],
          selectedLabels: { ...(current.selectedLabels ?? {}), [item.value]: selectedLabel },
          touched: true,
        };
      });
      setIsDropdownOpen(true);
      inputRef.current?.focus();
      return;
    }

    updateFilterState(filter.id, current => ({
      ...current,
      inputValue: item.value,
      displayValue: selectedLabel,
      selectedLabels: { ...(current.selectedLabels ?? {}), [item.value]: selectedLabel },
      touched: true,
    }));
    setIsDropdownOpen(false);
  };

  const toggleSearchMode = () => {
    updateFilterState(filter.id, current => ({
      ...current,
      searchMode: (current.searchMode ?? 'contains') === 'contains' ? 'startsWith' : 'contains',
    }));
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        {inputValue && filter.multiple ? (
          <button
            type="button"
            className="absolute inset-y-0 right-3 flex items-center text-cool-grey hover:text-charcoal-grey"
            title="Limpiar texto de búsqueda"
            onClick={() => {
              updateFilterState(filter.id, current => ({
                ...current,
                inputValue: '',
                displayValue: '',
              }));
              inputRef.current?.focus();
            }}
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <path d="M5 5L15 15M15 5L5 15" strokeLinecap="round" />
            </svg>
          </button>
        ) : (
          <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-cool-grey">
            <svg
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
            >
              <circle cx="9" cy="9" r="6.25" />
              <path d="M13.5 13.5L18 18" strokeLinecap="round" />
            </svg>
          </span>
        )}
        {/*
          Sin `autoComplete="off"` Safari ofrece rellenar esto con los contactos del usuario.
          Elige el campo mirando, en este orden, `name`, `placeholder` y la etiqueta: aquí no hay
          `name`, el placeholder es genérico, y acaba cayendo en la etiqueta del filtro — "País de
          origen" o "Nombre del proveedor" le parecen un campo de contacto. Por eso tampoco se le
          pone `name`: Safari abre el menú de contactos cuando contiene un guion, así que un
          `name="filtro-<id>"` empeoraría justo lo que se está arreglando.
        */}
        <input
          ref={inputRef}
          className={`${inputClassName} pr-9`}
          type="text"
          autoComplete="off"
          placeholder="Buscar..."
          value={inputValue}
          disabled={filtersDisabled}
          onFocus={() => {
            updateFilterState(filter.id, current => ({ ...current, touched: true }));
            setIsDropdownOpen(true);
            // Antes se pedían opciones con término vacío al enfocar. Esa
            // petición obligaba al backend a recorrer la tabla entera y era el
            // origen de los errores 502. Las opciones se cargan al escribir.
          }}
          onChange={event => {
            const value = event.target.value;
            setIsDropdownOpen(true);
            const matchedItem = findMatchedItem(value);

            if (matchedItem) {
              handleSelectItem(matchedItem);
              return;
            }

            updateFilterState(filter.id, current => ({
              ...current,
              inputValue: value,
              displayValue: value,
              touched: true,
            }));

            if (filterOffersOptionList(filter)) {
              scheduleFetchOptions(value);
            }
          }}
          onKeyDown={event => {
            if (!filter.multiple) return;
            if (event.key !== 'Enter') return;
            event.preventDefault();
            const value = (state?.displayValue ?? state?.inputValue ?? '').trim();
            if (!value) return;

            const matchedItem = findMatchedItem(value);
            const nextValue = matchedItem?.value ?? value;
            const nextLabel = matchedItem ? getFilterOptionSelectedLabel(matchedItem) : value;

            updateFilterState(filter.id, current => {
              if (current.selectedValues.includes(nextValue)) {
                return { ...current, touched: true };
              }
              return {
                ...current,
                selectedValues: [...current.selectedValues, nextValue],
                selectedLabels: { ...(current.selectedLabels ?? {}), [nextValue]: nextLabel },
                touched: true,
              };
            });
            setIsDropdownOpen(true);
          }}
        />

        {isDropdownOpen && filteredItems.length > 0 && (
          <div
            ref={dropdownRef}
            className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-y-auto rounded-lg border border-pale-grey-three bg-white shadow-lg"
          >
            <div className="flex items-center justify-end gap-2 border-b border-pale-grey-three px-3 py-1.5">
              <InstantTooltip
                text={
                  searchMode === 'contains'
                    ? 'Da click para cambiar a buscar registros que empiecen por el texto ingresado'
                    : 'Da click para cambiar a buscar registros que contengan el texto ingresado'
                }
                position="bottom"
                align="end"
              >
                <button
                  type="button"
                  className="rounded-md bg-primary px-2.5 py-1 text-[11px] font-medium text-white transition hover:bg-blue-800"
                  onClick={toggleSearchMode}
                >
                  {searchMode === 'contains' ? 'Contiene' : 'Empieza por'}
                </button>
              </InstantTooltip>
              {filter.multiple && filteredItems.length > 1 && (
                <button
                  type="button"
                  className="text-[11px] font-medium text-primary hover:text-blue-800"
                  onClick={handleAddAll}
                >
                  Agregar todos ({filteredItems.length})
                </button>
              )}
            </div>
            {filteredItems.map(item => {
              const displayLabel = getFilterOptionDisplayLabel(item);
              const isSelected = state?.selectedValues.includes(item.value);
              return (
                <button
                  key={`${item.value}-${displayLabel}`}
                  type="button"
                  className={`block w-full px-3 py-1.5 text-left text-xs transition hover:bg-pale-grey ${
                    isSelected ? 'bg-primary/5 font-semibold text-primary' : 'text-charcoal-grey'
                  }`}
                  onClick={() => handleSelectItem(item)}
                >
                  {displayLabel}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {filter.multiple && (state?.selectedValues.length ?? 0) > 0 ? (
        <div className="space-y-1">
          {(state?.selectedValues.length ?? 0) > 1 && (
            <button
              type="button"
              className="rounded-full border border-red-200 bg-red-50 px-3 py-1 text-xs font-medium text-red-600 hover:bg-red-100"
              disabled={filtersDisabled}
              onClick={handleRemoveAll}
            >
              Quitar todos
            </button>
          )}
          <div
            ref={chipsRef}
            className={`flex flex-wrap items-center gap-2 ${!chipsExpanded ? 'max-h-[84px] overflow-hidden' : ''}`}
          >
            {state?.selectedValues.map(value => (
              <button
                key={value}
                type="button"
                className="rounded-full border border-pale-grey-three bg-pale-grey px-3 py-1 text-xs text-slate"
                disabled={filtersDisabled}
                onClick={() =>
                  updateFilterState(filter.id, current => {
                    const selectedLabels = { ...(current.selectedLabels ?? {}) };
                    delete selectedLabels[value];

                    return {
                      ...current,
                      selectedValues: current.selectedValues.filter(item => item !== value),
                      selectedLabels,
                      touched: true,
                    };
                  })
                }
              >
                {getSelectedFilterLabel(state, value)} ×
              </button>
            ))}
          </div>
          {(chipsOverflow || chipsExpanded) && (
            <button
              type="button"
              className="text-xs font-medium text-primary hover:text-blue-800"
              onClick={() => setChipsExpanded(prev => !prev)}
            >
              {chipsExpanded ? 'Ocultar' : `Ver todos (${state?.selectedValues.length ?? 0})`}
            </button>
          )}
        </div>
      ) : null}
    </div>
  );
};

export const LookupFilterField = withFilterFieldBase(LookupFilterFieldContent, props => {
  const inputValue = props.state?.displayValue ?? props.state?.inputValue ?? '';
  const showTypeaheadHint = Boolean(inputValue.trim()) && filterOffersOptionList(props.filter);

  return {
    label: props.label,
    showGuidedText: props.showGuidedText,
    guidedText: props.filter.guidedText,
    showTypeaheadHint,
    isFetching: props.state?.isFetching,
  };
});
